import { useEffect, useState } from 'react';

/**
 * THE UPDATE'S PICTURE (William, 17 Oct 2026 12:4x AM: "can you then fix it and provide a bar graph for showing it is
 * working"). While the queue's Update runs, a moving bar with the seconds so far (the API answers once, so there is no
 * percentage to show); when it answers, one bar per outcome with its number, and the rosters the server read.
 * Plain CSS bars (index.css, the .upd- classes), no chart library.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */

export interface UpdateCounts {
  matched: number;
  ambiguous: number;
  unmatched: number;
  skipped?: number;
  suggested?: number;
  campaigns?: Record<string, number>;
}

export const UPDATE_GRAPH_WORDING = {
  running: (clientName: string, seconds: number): string => `Updating ${clientName}: ${seconds}s`,
  runningLabel: (clientName: string): string => `Update running for ${clientName}`,
  graphLabel: (clientName: string): string => `Update result for ${clientName}`,
  rosters: (campaigns: Record<string, number>): string => {
    const list = Object.entries(campaigns)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([slug, n]) => `${slug || 'no campaign'} ${n}`)
      .join(', ');
    return `Rosters read: ${list || 'none'}`;
  },
} as const;

/** The bars, in his order. `tone` picks the colour class. */
export function updateBars(d: UpdateCounts): { key: string; label: string; count: number; tone: string }[] {
  return [
    { key: 'matched', label: 'Matched', count: d.matched ?? 0, tone: 'ok' },
    { key: 'suggested', label: 'Suggested', count: d.suggested ?? 0, tone: 'suggest' },
    { key: 'ambiguous', label: 'Ambiguous', count: d.ambiguous ?? 0, tone: 'warn' },
    { key: 'unmatched', label: 'Unmatched', count: d.unmatched ?? 0, tone: 'bad' },
    { key: 'skipped', label: 'Skipped', count: d.skipped ?? 0, tone: 'muted' },
  ];
}

/** The moving bar while the Update runs, with whole seconds since it started. */
export function UpdateRunningBar({ clientName, startedAt }: { clientName: string; startedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  return (
    <div className="upd-running" data-testid="update-running">
      <div className="mc-progress upd-progress" role="progressbar" aria-label={UPDATE_GRAPH_WORDING.runningLabel(clientName)} />
      <div className="micro muted" data-testid="update-elapsed">{UPDATE_GRAPH_WORDING.running(clientName, seconds)}</div>
    </div>
  );
}

/** The result: one horizontal bar per outcome, scaled to the largest, each with its number. */
export function UpdateResultGraph({ clientName, data }: { clientName: string; data: UpdateCounts }) {
  const bars = updateBars(data);
  const max = Math.max(1, ...bars.map((b) => b.count));
  return (
    <figure className="upd-graph" data-testid="update-graph" aria-label={UPDATE_GRAPH_WORDING.graphLabel(clientName)}>
      {bars.map((b) => (
        <div key={b.key} className="upd-row" data-testid={`update-bar-${b.key}`} data-count={b.count}>
          <span className="upd-label">{b.label}</span>
          <span className="upd-track">
            <span className={`upd-bar upd-${b.tone}`} style={{ width: `${(b.count / max) * 100}%` }} />
          </span>
          <span className="upd-num">{b.count}</span>
        </div>
      ))}
      {data.campaigns && (
        <figcaption className="micro muted" data-testid="update-rosters">{UPDATE_GRAPH_WORDING.rosters(data.campaigns)}</figcaption>
      )}
    </figure>
  );
}
